import { ESRI_GEOCODE_SERVICE_URL } from '@rsos/maps/interface/reverseGeocode/helpers';
import { getAddressFromMagicKey } from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import { MAX_POI } from './GlobalMapSearch.constants';
import type { PlaceCandidate } from './GlobalMapSearch.types';
import { normalizePlaceCoords } from './GlobalMapSearch.utils';

export type AbortableThunkResult = Promise<Record<string, unknown>> & {
  abort?: () => void;
  unsubscribe?: () => void;
};

export type ThunkDispatch = (action: unknown) => AbortableThunkResult;

export interface SuggestEntry {
  text: string;
  magicKey: string;
  _serviceUrl?: string;
  _token?: string | null;
}

/**
 * RTK Query dispatch results expose `abort()` for in-flight requests. Bridge
 * the search orchestrator's AbortSignal into those requests so a newer
 * keystroke cancels stale suggestion work instead of only ignoring the result.
 */
export function abortThunkOnSignal(
  request: AbortableThunkResult,
  signal: AbortSignal,
): () => void {
  if (signal.aborted) {
    request.abort?.();
    return () => undefined;
  }

  const abort = () => request.abort?.();
  signal.addEventListener('abort', abort, { once: true });
  return () => signal.removeEventListener('abort', abort);
}

/**
 * Build a stable dedupe key for place rows. Address-only dedupe is too coarse
 * for POIs because many distinct businesses can share the same street address.
 */
export function buildPlaceDedupKey(candidate: PlaceCandidate): string {
  const attrs = candidate.attributes || {};
  const shortLabel =
    typeof attrs.ShortLabel === 'string'
      ? attrs.ShortLabel.trim().toLowerCase()
      : '';
  const placeName =
    typeof attrs.PlaceName === 'string'
      ? attrs.PlaceName.trim().toLowerCase()
      : '';
  const longLabel =
    typeof attrs.LongLabel === 'string'
      ? attrs.LongLabel.trim().toLowerCase()
      : '';
  const address = (candidate.address || '').trim().toLowerCase();
  const x =
    typeof candidate.location?.x === 'number'
      ? candidate.location.x.toFixed(6)
      : '';
  const y =
    typeof candidate.location?.y === 'number'
      ? candidate.location.y.toFixed(6)
      : '';
  return [address, shortLabel, placeName, longLabel, x, y].join('|');
}

interface FetchAddressCandidatesArgs {
  text: string;
  lng?: number;
  lat?: number;
  serviceUrl?: string;
  token?: string | null;
  signal: AbortSignal;
}

/**
 * Fetch direct `/findAddressCandidates` hits for a free-text location query.
 * Runs local geocoder first (when configured), then supplements with ESRI world
 * for remaining slots. Used for the generic "location search" path so we don't
 * rely solely on `/suggest`.
 */
export async function fetchAddressCandidates({
  text,
  lng,
  lat,
  serviceUrl,
  token,
  signal,
}: FetchAddressCandidatesArgs): Promise<PlaceCandidate[]> {
  const fetchCandidates = async ({
    baseUrl,
    maxLocations,
    token: requestToken,
  }: {
    baseUrl: string;
    maxLocations: number;
    token?: string | null;
  }): Promise<PlaceCandidate[]> => {
    if (maxLocations <= 0) return [];
    try {
      const params: Record<string, string> = {
        address: text,
        outFields: '*',
        maxLocations: String(maxLocations),
        f: 'json',
      };
      if (lng != null && lat != null) params.location = `${lng},${lat}`;
      if (requestToken) params.token = requestToken;
      const res = await fetch(
        `${baseUrl}/findAddressCandidates?${new URLSearchParams(params)}`,
        { signal },
      );
      if (!res.ok) return [];
      const json = await res.json();
      const candidates = Array.isArray(json?.candidates)
        ? (json.candidates as PlaceCandidate[])
        : [];
      return candidates
        .filter(c => !!c?.address && !!c?.location)
        .map(c =>
          normalizePlaceCoords({
            ...c,
            _serviceUrl: baseUrl,
            _token: requestToken ?? null,
          }),
        );
    } catch {
      return [];
    }
  };

  const localCandidates = serviceUrl
    ? await fetchCandidates({
        baseUrl: serviceUrl,
        maxLocations: MAX_POI,
        token,
      })
    : [];
  if (signal.aborted) return [];

  const remaining = Math.max(0, MAX_POI - localCandidates.length);
  const esriCandidates = await fetchCandidates({
    baseUrl: ESRI_GEOCODE_SERVICE_URL,
    maxLocations: remaining,
  });
  if (signal.aborted) return [];

  const seen = new Set<string>();
  const combined: PlaceCandidate[] = [];
  for (const candidate of [...localCandidates, ...esriCandidates]) {
    const key = buildPlaceDedupKey(candidate);
    if (seen.has(key)) continue;
    seen.add(key);
    combined.push(candidate);
  }
  return combined.slice(0, MAX_POI);
}

interface ResolveSuggestionsArgs {
  suggestions: SuggestEntry[];
  seen: Set<string>;
  dispatch: ThunkDispatch;
  signal: AbortSignal;
}

/**
 * Resolve /suggest results into full PlaceCandidates via magicKey lookup,
 * skipping any addresses already in `seen`.
 */
export async function resolveSuggestions({
  suggestions,
  seen,
  dispatch,
  signal,
}: ResolveSuggestionsArgs): Promise<PlaceCandidate[]> {
  const results = await Promise.all(
    suggestions
      .filter(s => s.magicKey)
      .map(async s => {
        let cleanupAbortListener: (() => void) | undefined;
        try {
          if (signal.aborted) return null;
          const request = dispatch(
            getAddressFromMagicKey.initiate({
              magicKey: s.magicKey,
              text: s.text,
              _serviceUrl: s._serviceUrl,
              _token: s._token,
            }),
          );
          cleanupAbortListener = abortThunkOnSignal(request, signal);
          const res = (await request) as unknown as { data?: PlaceCandidate };
          if (signal.aborted) return null;
          if (!res?.data?.location) return null;
          return normalizePlaceCoords({
            ...res.data,
            _serviceUrl: s._serviceUrl,
            _token: s._token,
          });
        } catch {
          return null;
        } finally {
          cleanupAbortListener?.();
        }
      }),
  );

  if (signal.aborted) return [];

  const resolved: PlaceCandidate[] = [];
  for (const r of results) {
    if (!r?.address || !r.location) continue;
    const key = buildPlaceDedupKey(r);
    if (seen.has(key)) continue;
    seen.add(key);
    resolved.push(r);
  }
  return resolved;
}
